// Custom app accent color. The whole UI reads --color-primary (+ -dark / -light)
// from CSS, so changing the user's accent = overriding those three variables on
// <html>. Priority colours (red/yellow/green) and sub-calendar colours are
// separate and intentionally NOT affected.

/**
 * Ten colours in Silvia's order and the order of the wheel, five shades each
 * (brown four — see below):
 *
 *   1 pastel ─ 2 soft ─ 3 full ─ 4 deep ─ 5 ACCESO
 *
 * The first four descend in lightness — that is the scale, and it has to be
 * unbroken or the grid stops reading as a grid. The fifth is the loud one, and
 * it sits at the END rather than inside the ramp: put fourth, it interrupted
 * the climb with a flash and then went dark again, which is exactly the 'the
 * scale is gone' Silvia noticed.
 *
 * Pink, violet, blue, light blue and green are Silvia's exceptions to that,
 * placed by her eye rather than by the pattern: she moved their loud shade to
 * where she wants it seen. The pattern is a default, not a law — but the
 * measuring below still applies to every row however it is ordered, because two
 * dots side by side that look like one dot is a fault in any arrangement.
 *
 * Every value is generated from HSL and then CHECKED, not chosen by eye, against
 * four rules — because eyes are the thing that was wrong here repeatedly:
 *
 *   1. no two shades side by side in a row within ~12 perceptual units;
 *   2. the first four of every row genuinely descend in lightness;
 *   3. the FIRST shade of each colour distinguishable from every other first;
 *   4. the loud fifth distinguishable from the fourth — otherwise the row ends
 *      in the same dot printed twice.
 *
 * Rule 3 is the one that cost several attempts. The pale end started at 82%
 * lightness, where a hue barely exists: red, orange and pink came out as the
 * same rosy fog. Red only became red again by moving its hue off 2° onto 8°,
 * warm enough to be told from the pink at 332° without becoming an orange.
 *
 * Then Silvia could not see the first yellow at all — the pale end was still
 * more white than colour. The warm rows were regenerated with far more
 * saturation and less white, orange sitting lower than yellow so the two do not
 * meet at the top, and brown dropped its palest shade entirely: a pale brown IS
 * an orange, and it was colliding with one. Brown therefore has four.
 *
 * Rule 4 came last and caught two rows nobody had complained about. Red ended
 * on two dark reds 7 apart, and blue on two navies — the blue row had its loud
 * shade stranded in the middle of the ramp, which is why the row looked like it
 * lost its way. Same five blues, put in the right order.
 *
 * Last: orange moved off 28° onto 34° and its ramp was stretched from a 30-point
 * lightness span to 36. At 28° and 84% lightness the first dot read as a salmon
 * — which is to say a pink, the same trap the red fell into and for the same
 * reason: near white, a warm hue loses the thing that tells it from its
 * neighbours. Widening the span was needed because a warm ramp needs bigger
 * steps: three even gaps across 30 points came out 10 or 11 apart, under the
 * threshold, however the hue was chosen.
 *
 * Two colours cannot have a loud version and the code does not pretend they do.
 * A brown at full saturation is rust; a grey at full saturation is not a grey.
 * Theirs is the most solid version still honestly that colour.
 */
export const ACCENT_COLORS = [
  '#fcefba', '#fce792', '#fcdb54', '#fdcf17', '#feb10b',   // yellow
  '#f9ce94', '#f9b358', '#f9991a', '#d37903', '#fb5f04',   // orange
  '#f6b6ac', '#f48b7b', '#f36049', '#d72509', '#f82512',   // red
  '#efc3d3', '#e694b2', '#de5487', '#ee2f88', '#bc2f63',   // pink
  '#d8bce6', '#bf8cd9', '#a14dcb', '#a82cdd', '#7c389f',   // violet
  '#94bae6', '#5c95d6', '#0987e1', '#396fac', '#1b496a',   // blue
  '#8ed2eb', '#56b8dc', '#0db5f2', '#3590b1', '#21708c',   // light blue
  '#9de7b8', '#5ddf8d', '#20c55d', '#0ec742', '#12873d',   // green — not sage
  '#bc9076', '#9a684c', '#9d5125', '#61412e',   // brown, four: see rule 3
  '#b5bbc5', '#8e97a4', '#67717e', '#464d59', '#242a32',   // grey
  // No sage row. MeMo is already sage, so five shades of it here were five
  // ways of choosing what you have; the single dot after the greys — the one
  // that puts MeMo back to its own green — is the whole of what sage means in
  // this grid.
];

// The PWA status-bar colour (meta[name=theme-color]) — the dark strip at the
// very top of the phone. Kept when the user reverts to the default sage.
const DEFAULT_THEME_COLOR = '#1a4a35';

function hexToRgb(h) {
  h = String(h).replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
}
// amt < 0 darkens (towards black), amt > 0 lightens (towards white)
function shade(hex, amt) {
  const [r, g, b] = hexToRgb(hex);
  const t = amt < 0 ? 0 : 255;
  const p = Math.abs(amt);
  return rgbToHex(r + (t - r) * p, g + (t - g) * p, b + (t - b) * p);
}

// Apply (or clear) the accent by overriding the CSS variables on <html>.
// Also recolours the app logo (--logo-from / --logo-to) and the phone status
// bar (meta[name=theme-color]) so the whole chrome follows the chosen colour.
export function applyAccent(hex) {
  const root = document.documentElement;
  const vars = ['--color-primary', '--color-primary-dark', '--color-primary-light', '--logo-from', '--logo-to'];
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!hex) {
    vars.forEach(v => root.style.removeProperty(v));
    if (meta) meta.setAttribute('content', DEFAULT_THEME_COLOR);
    return; // revert to default sage + green logo + green status bar
  }
  try {
    root.style.setProperty('--color-primary', hex);
    root.style.setProperty('--color-primary-dark', shade(hex, -0.20));
    root.style.setProperty('--color-primary-light', shade(hex, 0.24));
    root.style.setProperty('--logo-from', shade(hex, -0.30));
    root.style.setProperty('--logo-to', shade(hex, 0.20));
    if (meta) meta.setAttribute('content', shade(hex, -0.30));
  } catch (_) {}
}
